import { CartItem, OBSObjI, PrepaidPaymentProps } from '@msc-b2c/interfaces/types';
import {
  bookingApi,
  clearAppliedECoupon,
  closeLoader,
  getActiveSession,
  getQueryParam,
  isElementVisible,
  mscGet,
  OnPriceToBookOptions,
  openLoader,
  PaymentContext,
  PrepaidBookingMyMSC,
  prepaidConfirmation,
  RootState,
  setErrorManagerCode,
  setPaymentCouponErrorCode,
  store,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { getPricesCart } from '../../utils/getPricesCart';
import {
  isAppliedCouponStillValid,
  resolveInvalidAppliedCouponIssueCode,
  resolveInvalidAppliedCouponIssueCodeFromError,
} from '../../utils/appliedCouponValidation';
import {
  isCartAvailabilityErrorCode,
  redirectToCartWithAvailabilityError,
} from '../../utils/cartAvailabilityUtils';
import {
  extractEcouponIssueCodeFromError,
  GENERIC_ECOUPON_PAYMENT_ERROR_CODE,
  isConfiguredEcouponErrorCode,
  isHandledPaymentCouponErrorCode,
  normalizeEcouponErrorCode,
} from '../../utils/ecouponErrorUtils';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ExcursionPrepaidBookingMyMSCRequest,
  PrepaidBookingMyMSCRequest,
  PrepaidBookingMyMSCResponse,
} from '@msc-b2c/interfaces/interfaces';
import {
  getObsForBookingFromCart,
  hasAllInclusiveExcursionsInCart,
  hasAllInclusiveOrCombinedInCart,
} from '../../../utils/myAreaCartUtils';
import { refreshAllInclusiveCatalog } from '../../../utils/refreshAllInclusiveCatalog';
import { Payment } from './Payment';
import { LoginMode } from '@msc-b2c/interfaces/enums';
import {
  getPaymentTypeFromSession,
  triggerPaymentFailureEvent,
  triggerPurchaseEvent,
} from '../../../utils/myAreaAnalytics';
import { useNavigate } from 'react-router-dom';
import {
  CheckoutEmailValidationEvent,
  CHECKOUT_EMAIL_VALIDATION_EVENT,
} from '../../../utils/myAreaUtils';

type PaymentDataType = {
  transactionId?: string;
};

export function PrepaidPayment({
  paymentMethodsTitle,
  paymentSectionTitle,
  paymentSectionDescription,
  paymentSectionFreeCartDescription,
  dateFormat,
  cardPaymentDetailsLabel,
  cardNumberPlaceholder,
  cardExpirationDatePlaceholder,
  cardCvvPlaceholder,
  cardCvvDisclaimer,
  mandatoryLabel,
  termsAndConditionSection,
  paymentButtonCTA,
  datatrans,
  emptyCartRedirectionLink,
  ecouponErrorMessageConfigList,
  ecouponErrorMessageConfigListResources,
}: Readonly<PrepaidPaymentProps>) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cruiseId, bookingNumber = '', passengerId = '', email, loginMode } = getActiveSession();
  const paymentErrorOccurred = useRef(false);
  const pendingPaymentFailureTransaction = useRef<string | null>(null);
  const trackedPaymentFailureTransaction = useRef<string | null>(null);
  const [result3dsPayment, setResult3dsPayment] = useState<{
    status: string | null;
    transactionId: string | null;
  } | null>(() => {
    const status = getQueryParam('status_3d');
    const transactionId = getQueryParam('uppTransactionId');

    return {
      status,
      transactionId,
    };
  });

  const membershipData = useSelector((state: RootState) => state.myCruise.membershipData);
  const cabinDetails = useSelector((state: RootState) => state.myCruise.cabinDetails);
  const excursions = useSelector((state: RootState) => state.myCruise.excursions?.excursions || []);
  const obs = useSelector((state: RootState) => state.myCruise.obs?.onboardServices || []);
  const allInclusiveCmsItems = useSelector(
    (state: RootState) => state.myCruise.allInclusiveCmsItems
  );
  const disableEmbarkAndDisembarkExcursions = useSelector(
    (state: RootState) =>
      state.myAreaCommonConfig.prePaidCardProps.disableEmbarkAndDisembarkExcursions
  );
  const { alreadyPurchasedSectionRouteMapping } = useSelector(
    (state: RootState) => state.navigationMenuData
  );
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);
  const {
    items: cart,
    appliedECoupon,
    prices,
  } = useSelector((state: RootState) => state.myAreaCart);

  const getCheckoutEmail = useCallback(
    () => (loginMode === LoginMode.FULL ? email : store.getState().myAreaCart.loggedUserEmailInput),
    [email, loginMode]
  );

  const validateCheckoutEmail = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        if (loginMode !== LoginMode.LIGHT || !isElementVisible('.cmp-sidesummary__email-section')) {
          resolve(true);
          return;
        }

        let hasResponded = false;
        const validationEvent: CheckoutEmailValidationEvent = new CustomEvent(
          CHECKOUT_EMAIL_VALIDATION_EVENT,
          {
            detail: {
              respond: (isValid: boolean) => {
                hasResponded = true;
                resolve(isValid);
              },
            },
          }
        );

        window.dispatchEvent(validationEvent);

        window.setTimeout(() => {
          if (!hasResponded) {
            resolve(Boolean(getCheckoutEmail()?.trim()));
          }
        }, 0);
      }),
    [getCheckoutEmail, loginMode]
  );

  const getExcursions = (items: CartItem[]) => {
    return items
      .filter((cartItem) => cartItem.itemType === 'EXCURSION')
      .map(
        (cartItem) =>
          ({
            code: cartItem.code,
            startDate: cartItem.startDate,
            priceBasis: cartItem.priceBasis,
            ...(cartItem.priceBasis === 'PER_PERSON' && {
              passengers: cartItem.passengers,
            }),
            ...(cartItem.priceBasis === 'PER_CABIN' && { cabins: cartItem.cabins }),
            ...(cartItem.promotion && { promotion: cartItem.promotion }),
          } as CartItem)
      );
  };

  const trackPurchase = (prepaidBookingResponse: PrepaidBookingMyMSCResponse) => {
    const sessionPaymentData = mscGet<PaymentDataType>('session', 'paymentData');
    const transactionId = sessionPaymentData?.transactionId;

    if (!booking || !transactionId) {
      return;
    }

    const purchasedExcursionCodes = (prepaidBookingResponse.excursions || []).map(
      (exc) => exc.code
    );
    const purchasedOBSCodes = (prepaidBookingResponse.onboardServices || []).map((obs) => obs.code);

    const purchasedExcursions = (excursions || []).filter((excursion) =>
      purchasedExcursionCodes.some((excCode) => excCode === excursion.code)
    );

    const purchasedOBS = (obs || []).filter((obs) =>
      purchasedOBSCodes.some((obsCode) => obsCode === obs.code)
    );

    const purchasedItemsUnordered = [...purchasedExcursions, ...purchasedOBS];
    const purchasedItems = cart
      .map((cartItem) => {
        const item = purchasedItemsUnordered.find((purchased) => purchased.code === cartItem.code);
        if (!item) return undefined;
        const itemCategory2 =
          cartItem.itemType === 'EXCURSION'
            ? 'excursions'
            : alreadyPurchasedSectionRouteMapping
                ?.find((routeItem) =>
                  Object.keys(routeItem.routeTypes).includes((item as OBSObjI).type?.code ?? '')
                )
                ?.routeLabel.toLowerCase() ?? '';
        return {
          ...item,
          itemCategory2,
          itemListName: cartItem.itemListName,
          selectedPassengerQuantity: cartItem.passengers?.length,
        };
      })
      .filter((item) => !!item);

    const paymentAmount = prepaidBookingResponse.price?.total?.afterDiscount || '0';
    const totalDiscount = prepaidBookingResponse.price?.total?.discountAmount
      ? Number(prepaidBookingResponse.price.total.discountAmount)
      : prepaidBookingResponse.price?.total?.beforeDiscount &&
        prepaidBookingResponse.price?.total?.afterDiscount
      ? Number(prepaidBookingResponse.price.total.beforeDiscount) -
        Number(prepaidBookingResponse.price.total.afterDiscount)
      : 0;
    const paymentType = getPaymentTypeFromSession();

    const purchaseData = {
      booking: booking,
      items: purchasedItems,
      loggedUserId: passengerId,
      transactionId: transactionId,
      transactionType: 'full',
      paymentType: paymentType,
      paymentAmount: paymentAmount,
      membershipData: membershipData,
      coupon: appliedECoupon,
      totalDiscount: totalDiscount,
      cabinDetails: cabinDetails,
      checkoutPaymentEmail: getCheckoutEmail(),
    };

    triggerPurchaseEvent(purchaseData);
  };

  const trackPaymentFailure = useCallback(
    (transactionId: string) => {
      if (trackedPaymentFailureTransaction.current === transactionId) {
        return true;
      }

      if (!booking || !prices) {
        pendingPaymentFailureTransaction.current = transactionId;
        return false;
      }

      trackedPaymentFailureTransaction.current = transactionId;
      pendingPaymentFailureTransaction.current = null;

      const items = [...excursions, ...obs];
      const failedItems = cart
        .map((cartItem) => {
          const item = items.find((known) => known.code === cartItem.code);
          if (!item) return undefined;
          const itemCategory2 =
            cartItem.itemType === 'EXCURSION'
              ? 'excursions'
              : alreadyPurchasedSectionRouteMapping
                  ?.find((routeItem) =>
                    Object.keys(routeItem.routeTypes).includes((item as OBSObjI).type?.code ?? '')
                  )
                  ?.routeLabel.toLowerCase() ?? '';
          return {
            ...item,
            itemCategory2,
            itemListName: cartItem.itemListName,
            selectedPassengerQuantity: cartItem.passengers?.length,
          };
        })
        .filter((item) => !!item);

      const paymentAmount = prices?.price?.total?.afterDiscount?.toString() || '0';
      const totalDiscount = prices?.price?.total?.discountAmount
        ? Number(prices.price.total.discountAmount)
        : prices?.price?.total?.beforeDiscount && prices?.price?.total?.afterDiscount
        ? Number(prices.price.total.beforeDiscount) - Number(prices.price.total.afterDiscount)
        : 0;

      triggerPaymentFailureEvent({
        booking,
        items: failedItems,
        loggedUserId: passengerId,
        transactionId,
        transactionType: 'full',
        paymentType: getPaymentTypeFromSession(),
        paymentAmount,
        membershipData,
        coupon: appliedECoupon,
        totalDiscount,
        cabinDetails,
        checkoutPaymentEmail: getCheckoutEmail(),
      });

      return true;
    },
    [
      alreadyPurchasedSectionRouteMapping,
      appliedECoupon,
      booking,
      cabinDetails,
      cart,
      excursions,
      membershipData,
      obs,
      passengerId,
      prices,
      getCheckoutEmail,
    ]
  );

  useEffect(() => {
    if (result3dsPayment?.status !== 'N') {
      return;
    }

    const hasTrackedPaymentFailure = trackPaymentFailure(result3dsPayment.transactionId || '');

    if (hasTrackedPaymentFailure) {
      setResult3dsPayment(null);
    }
  }, [result3dsPayment, trackPaymentFailure]);

  useEffect(() => {
    if (!pendingPaymentFailureTransaction.current || !prices) {
      return;
    }

    trackPaymentFailure(pendingPaymentFailureTransaction.current);
  }, [prices, booking, trackPaymentFailure]);

  return (
    <PaymentContext.Provider
      value={{
        onFinalizePayment: async (data, onSuccess, onError, onFinally) => {
          const isCheckoutEmailValid = await validateCheckoutEmail();

          if (!isCheckoutEmailValid) {
            onError?.({ issue_code: 'CHECKOUT_EMAIL_REQUIRED' });
            return;
          }

          openLoader('.loader');
          pendingPaymentFailureTransaction.current = null;
          trackedPaymentFailureTransaction.current = null;
          const { transactionId } = data;
          const couponCode = store.getState().myAreaCart.appliedECoupon;
          const checkoutEmail = getCheckoutEmail();

          const handlePaymentFailure = (error: { issue_code?: string }) => {
            closeLoader('.loader');
            console.log('PrepaidBookingMyMSC - Error during payment finalization:', error);
            paymentErrorOccurred.current = true;

            const issueCode = normalizeEcouponErrorCode(
              error.issue_code ?? extractEcouponIssueCodeFromError(error)
            );

            if (issueCode && isCartAvailabilityErrorCode(issueCode)) {
              if (cruiseId && bookingNumber) {
                getPricesCart(dispatch, cruiseId, bookingNumber, true, undefined, {
                  handleAvailabilityErrors: false,
                  onCartAvailabilityError: (code) =>
                    redirectToCartWithAvailabilityError(code, emptyCartRedirectionLink, navigate),
                })
                  .catch(() =>
                    redirectToCartWithAvailabilityError(
                      issueCode,
                      emptyCartRedirectionLink,
                      navigate
                    )
                  )
                  .finally(() => undefined);
              } else {
                redirectToCartWithAvailabilityError(issueCode, emptyCartRedirectionLink, navigate);
              }
              trackPaymentFailure(transactionId);
              onError?.(error);
              return;
            }

            if (couponCode && issueCode) {
              const paymentCouponCode = isConfiguredEcouponErrorCode(
                issueCode,
                ecouponErrorMessageConfigList,
                ecouponErrorMessageConfigListResources
              )
                ? issueCode
                : GENERIC_ECOUPON_PAYMENT_ERROR_CODE;
              dispatch(setPaymentCouponErrorCode(paymentCouponCode));
            } else if (issueCode) {
              dispatch(setErrorManagerCode(issueCode));
            }

            trackPaymentFailure(transactionId);
            onError?.(error);
          };

          const request: PrepaidBookingMyMSCRequest = {
            ...(couponCode && { coupons: [{ code: couponCode }] }),
            cruise: { id: cruiseId! },
            passenger: {
              email: checkoutEmail!,
            },
            excursions: getExcursions(cart) as ExcursionPrepaidBookingMyMSCRequest[],
            onboardServices: getObsForBookingFromCart(),
          };
          if (!!transactionId) {
            request.transactionId = transactionId;
          }

          const finalizePayment = new PrepaidBookingMyMSC(false, false, request, {
            bookingNumber: bookingNumber!,
          });

          finalizePayment
            .sendData()
            .then((res: PrepaidBookingMyMSCResponse) => {
              dispatch(prepaidConfirmation(res));
              dispatch(bookingApi.util.invalidateTags(['Booking']));

              const shouldRefreshObs = hasAllInclusiveOrCombinedInCart(cart, obs);
              const shouldRefreshExcursions = hasAllInclusiveExcursionsInCart(
                cart,
                allInclusiveCmsItems
              );

              if (shouldRefreshObs || shouldRefreshExcursions) {
                void refreshAllInclusiveCatalog({
                  dispatch,
                  cruiseId,
                  bookingNumber,
                  refreshObs: shouldRefreshObs,
                  refreshExcursions: shouldRefreshExcursions,
                  includeDepartureAndArrivalPorts: !disableEmbarkAndDisembarkExcursions,
                });
              }

              trackPurchase(res);
              dispatch(clearAppliedECoupon());
              onSuccess?.(res);
            })
            .catch((error) => {
              handlePaymentFailure(error as { issue_code?: string });
            })
            .finally(() => {
              closeLoader('.loader');
              onFinally?.();
            });
        },
        onPriceToBook: (options?: OnPriceToBookOptions) => {
          const ecouponConfig = {
            ecouponErrorMessageConfigList,
            ecouponErrorMessageConfigListResources,
          };
          const paymentCouponErrorCode = store.getState().myAreaCart.paymentCouponErrorCode;
          const revalidateCoupon = options?.revalidateCouponAfterPaymentFailure;

          if (revalidateCoupon) {
            paymentErrorOccurred.current = true;
          }

          const skipPromoError = paymentErrorOccurred.current;
          paymentErrorOccurred.current = false;

          // Coupon field error is shown on checkout (SideSummary). Skip duplicate prices fetch
          // unless we are recovering after a failed payment (e.g. OTP return with invalid coupon).
          if (
            paymentCouponErrorCode &&
            isHandledPaymentCouponErrorCode(
              paymentCouponErrorCode,
              ecouponErrorMessageConfigList,
              ecouponErrorMessageConfigListResources
            ) &&
            !revalidateCoupon
          ) {
            return;
          }

          const pricesCartOptions = {
            handleAvailabilityErrors: false,
            onCartAvailabilityError: (code: string) =>
              redirectToCartWithAvailabilityError(code, emptyCartRedirectionLink, navigate),
          };

          const appliedCoupon = store.getState().myAreaCart.appliedECoupon;

          if (!revalidateCoupon || !appliedCoupon) {
            getPricesCart(
              dispatch,
              cruiseId!,
              bookingNumber!,
              skipPromoError,
              undefined,
              pricesCartOptions
            ).catch(() => undefined);
            return;
          }

          getPricesCart(
            dispatch,
            cruiseId!,
            bookingNumber!,
            skipPromoError,
            undefined,
            pricesCartOptions
          )
            .then((response) => {
              const invalidIssueCode = resolveInvalidAppliedCouponIssueCode(
                response,
                ecouponConfig
              );
              if (invalidIssueCode) {
                dispatch(setPaymentCouponErrorCode(invalidIssueCode));
                return;
              }

              if (!isAppliedCouponStillValid(response)) {
                dispatch(clearAppliedECoupon());
                return getPricesCart(
                  dispatch,
                  cruiseId!,
                  bookingNumber!,
                  skipPromoError,
                  undefined,
                  pricesCartOptions
                );
              }
            })
            .catch((error) => {
              const invalidIssueCode = resolveInvalidAppliedCouponIssueCodeFromError(
                error,
                ecouponConfig
              );
              if (invalidIssueCode) {
                dispatch(setPaymentCouponErrorCode(invalidIssueCode));
                return;
              }

              if (store.getState().myAreaCart.appliedECoupon) {
                dispatch(clearAppliedECoupon());
                getPricesCart(
                  dispatch,
                  cruiseId!,
                  bookingNumber!,
                  skipPromoError,
                  undefined,
                  pricesCartOptions
                ).catch(() => undefined);
              }
            });
        },
      }}
    >
      <Payment
        mandatoryLabel={mandatoryLabel}
        paymentSectionTitle={paymentSectionTitle ?? ''}
        paymentMethodsTitle={paymentMethodsTitle ?? ''}
        paymentSectionDescription={paymentSectionDescription ?? ''}
        paymentSectionFreeCartDescription={paymentSectionFreeCartDescription ?? ''}
        dateFormat={dateFormat ?? ''}
        cardPaymentDetailsLabel={cardPaymentDetailsLabel ?? ''}
        cardNumberPlaceholder={cardNumberPlaceholder ?? ''}
        cardExpirationDatePlaceholder={cardExpirationDatePlaceholder ?? ''}
        cardCvvPlaceholder={cardCvvPlaceholder ?? ''}
        cardCvvDisclaimer={cardCvvDisclaimer ?? ''}
        termsAndConditionSection={termsAndConditionSection ?? ''}
        paymentButtonCTA={paymentButtonCTA ?? ''}
        datatrans={datatrans}
      />
    </PaymentContext.Provider>
  );
}
